/**
 * Resuelve el filePath (root-relative, ej: `saas/github.yaml`) para precargar
 * el template edit-saas a partir de la annotation `backstage.io/location`.
 *
 * - `file:/Users/.../repo/catalog/saas/github.yaml` (absoluto, Windows o POSIX)
 *   y `file:catalog/saas/github.yaml` (local/E2E) → `saas/github.yaml`
 * - `url:https://github.com/PrisaMedia/backstage-catalog/blob/<branch>/saas/github.yaml`
 *   (desplegado, source backstage-catalog) → `saas/github.yaml`
 * - Cualquier otra location → undefined (el wizard pide la ruta a mano).
 */

// ponytail: copia literal del contrato server-side SAAS_PATH_PATTERN
// (plugins/scaffolder-backend-module-custom-update-yaml-document) — sin
// cross-package import; si cambia uno, cambia el otro.
const SAAS_YAML = /^saas\/[A-Za-z0-9][A-Za-z0-9._-]*\.ya?ml$/;

/** Devuelve la ruta solo si es root-relative `saas/*.yaml`, sin traversal. */
function rootRelativeSaasPath(path: string): string | undefined {
  return SAAS_YAML.test(path) ? path : undefined;
}

function resolveSaasFilePath(location: string | undefined): string | undefined {
  if (!location) return undefined;
  const sep = location.indexOf(":");
  if (sep <= 0) return undefined;
  const scheme = location.slice(0, sep);
  const target = location.slice(sep + 1);

  if (scheme === "file") {
    const normalized = target.replace(/\\/g, "/");
    // Extrae lo que va tras el último segmento `catalog/` (absoluto o relativo).
    const afterCatalog = normalized.match(/(?:.*\/)?catalog\/(.+)$/);
    if (afterCatalog) return rootRelativeSaasPath(afterCatalog[1]);
    // Sin `catalog/`: solo rutas relativas ya root-relative (`file:saas/x.yaml`).
    if (!normalized.startsWith("/")) {
      return rootRelativeSaasPath(normalized.replace(/^\.?\//, ""));
    }
    return undefined;
  }

  if (scheme === "url") {
    let url: URL;
    try {
      url = new URL(target);
    } catch {
      return undefined;
    }
    if (url.hostname.toLowerCase() !== "github.com") return undefined;
    let pathname: string;
    try {
      pathname = decodeURIComponent(url.pathname);
    } catch {
      return undefined;
    }
    const parts = pathname.split("/").filter(Boolean);
    // /<owner>/backstage-catalog/<blob|raw>/<ref>/<root-relative path>
    const [owner, repo, kind, ref, ...rest] = parts;
    if (!owner || !repo || !ref || (kind !== "blob" && kind !== "raw")) {
      return undefined;
    }
    if (repo.toLowerCase() !== "backstage-catalog") return undefined;
    if (ref === ".." || rest.some((seg) => seg === ".." || seg === ".")) {
      return undefined;
    }
    return rootRelativeSaasPath(rest.join("/"));
  }

  return undefined;
}

export { resolveSaasFilePath };
